import * as ExcelJS from 'exceljs';
import * as HtmlParsing from "./HtmlParsing";
import {aiStore} from "../../index";
import {handleAbort} from "./PdfParser";

async function readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function isImageValue(val) {
    return typeof val === 'string' && (
        val.trim().match(/\.(jpeg|jpg|png|gif|webp)$/i) ||
        val.startsWith('data:image/')
    );
}

async function handleImageValue(val, file, imageIndex) {
    try {
        if (val.startsWith('data:image/')) {
            return { success: true, src: val };
        } else if (val.trim().match(/\.(jpeg|jpg|png|gif|webp)$/i)) {
            if (val.startsWith('http')) {
                return { success: true, src: val };
            } else {
                try {
                    const response = await fetch(val);
                    if (response.ok) {
                        const blob = await response.blob();
                        const extension = val.split('.').pop().toLowerCase();
                        const fileId = await HtmlParsing.uploadImageFromFile(blob, file, imageIndex, extension);

                        if (fileId) {
                            return { success: true, src: fileId };
                        }
                    }
                } catch (error) {
                }

                return { success: true, src: val };
            }
        }
        return { success: false, error: 'Invalid image format' };
    } catch (error) {
        return { success: false, error: error.message };
    }
}

async function writeChunkToHtml(file, rows, headers, page, imageIndex, sheetName) {
    let html = `<h2>Sheet: ${sheetName}, Page: ${page}</h2>`;
    html += '<table border="1"><thead><tr>';
    headers.forEach(h => html += `<th>${escapeHtml(h)}</th>`);
    html += '</tr></thead><tbody>';

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        html += '<tr>';

        for (let j = 0; j < headers.length; j++) {
            const val = row[j];

            if (isImageValue(val)) {
                const imageResult = await handleImageValue(val, file, imageIndex);
                if (imageResult.success) {
                    imageIndex++;
                    html += `<td><img src="${imageResult.src}" width="100" alt="Image ${imageIndex}"/></td>`;
                } else {
                    html += `<td>(Image failed: ${imageResult.error})</td>`;
                }
            } else {
                html += `<td>${escapeHtml(val ?? '')}</td>`;
            }
        }

        html += '</tr>';
    }

    html += '</tbody></table>';
    return html;
}

async function convertExcelToHtml(file, isEmbedding, abortedFiles, chunkSize = 100) {
    try {
        if (handleAbort(file, abortedFiles, isEmbedding)) {
            return [];
        }
        const buffer = await readFileAsArrayBuffer(file);
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        const chunks = [];
        let globalPageIndex = 1;
        let imageIndex = 1;
        let totalRows = 0;
        let processedRows = 0;


        workbook.eachSheet(worksheet => {
            totalRows += Math.max(0, worksheet.rowCount - 1);
        });


        workbook.eachSheet(worksheet => {
            const sheetName = worksheet.name;
            const json = [];

            worksheet.eachRow((row, rowNumber) => {
                const rowValues = [];
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    rowValues[colNumber - 1] = cell.value;
                });
                json.push(rowValues);
            });

            if (json.length === 0) return;

            const headers = json[0];

            for (let i = 1; i < json.length; i += chunkSize) {

                if (handleAbort(file, abortedFiles, isEmbedding)) {
                    return [];
                }
                const rows = json.slice(i, i + chunkSize);
                const htmlChunkPromise = writeChunkToHtml(
                    file,
                    rows,
                    headers,
                    globalPageIndex++,
                    imageIndex,
                    sheetName
                );
                chunks.push(htmlChunkPromise);

                processedRows += rows.length;
                aiStore.sendAiProgressLoaderValue(file.name, (processedRows * 100) / totalRows);
            }
        });

        const resolvedChunks = await Promise.all(chunks);

        if (typeof HtmlParsing !== 'undefined' && HtmlParsing.splitHtmlIntoChunks) {
            const allHtml = resolvedChunks.join('');
            return HtmlParsing.splitHtmlIntoChunks(allHtml);
        }

        return resolvedChunks;
    } catch (error) {
        throw error;
    }
}

const convertExcelToText = async (file) => {
    const buffer = await readFileAsArrayBuffer(file);
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const lines = [];
    workbook.eachSheet((worksheet) => {
        worksheet.eachRow((row) => {
            const values = [];
            row.eachCell({includeEmpty: true}, (cell) => {
                if (isImageValue(String(cell.value ?? ''))) return;
                values.push(cell.value ?? '');
            });
            lines.push(values.join(','));
        });
    });
    return lines.join('\n');
};

export {convertExcelToHtml, convertExcelToText};